import { useState, useEffect, useCallback } from 'react';
import { CreditCard, RefreshCw, Download, DollarSign, CheckCircle2, Clock, RotateCcw, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  unpaid: 'bg-amber-50 text-amber-700 border-amber-200',
  initiated: 'bg-amber-50 text-amber-700 border-amber-200',
  refunded: 'bg-rose-50 text-rose-700 border-rose-200',
  expired: 'bg-gray-100 text-gray-600 border-gray-200',
};

const money = (n, cur) => `${(cur || 'usd').toUpperCase() === 'USD' ? '$' : ''}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Stat = ({ label, value, Icon, accent }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`billing-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</span>
      {Icon && <Icon className={`w-4 h-4 ${accent || 'text-gray-400'}`} />}
    </div>
    <div className="mt-2 text-2xl font-semibold text-gray-900">{value}</div>
  </div>
);

export default function BillingTab({ sessionToken }) {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [msg, setMsg] = useState('');

  const H = { Authorization: `Bearer ${sessionToken}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = statusFilter ? `?status=${statusFilter}` : '';
      const r = await fetch(`${API}/v1/admin/billing/payments${q}`, { headers: H }).then((x) => x.json());
      setPayments(r.payments || []);
      setSummary(r.summary || null);
    } finally {
      setLoading(false);
    }
  }, [sessionToken, statusFilter]); // eslint-disable-line

  useEffect(() => { if (sessionToken) load(); }, [sessionToken, load]);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 4000); };

  const exportCsv = () => fetch(`${API}/v1/admin/billing/export`, { headers: H })
    .then((r) => r.blob())
    .then((b) => {
      const u = URL.createObjectURL(b);
      const a = document.createElement('a');
      a.href = u; a.download = `kgob_billing_${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(u);
    });

  const reconcile = async () => {
    setReconciling(true);
    try {
      const r = await fetch(`${API}/v1/admin/health-check/reconcile-payments`, { method: 'POST', headers: H }).then((x) => x.json());
      flash(`Reconciled — checked ${r.checked}, finalized ${r.finalized}.`);
      await load();
    } finally {
      setReconciling(false);
    }
  };

  const refund = async (p) => {
    if (!window.confirm(`Refund ${money(p.amount, p.currency)} to ${p.client_name || p.email}? This issues a real Stripe refund.`)) return;
    const reason = window.prompt('Optional refund note (internal):', '') || '';
    const r = await fetch(`${API}/v1/admin/billing/refund`, {
      method: 'POST', headers: { ...H, 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: p.session_id, reason }),
    });
    if (r.ok) { flash('Refund issued.'); load(); }
    else { const e = await r.json().catch(() => ({})); flash(`Refund failed: ${e.detail || r.status}`); }
  };

  return (
    <div className="space-y-5" data-testid="tab-billing">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Billing</h2>
          <p className="text-sm text-gray-500">kgob.com sales ledger — every transaction labeled by product. app.kgob.com billing stays in app.kgob.com.</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={reconcile} disabled={reconciling} className="flex items-center gap-2 text-sm border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50" data-testid="billing-reconcile-btn" title="Finalize charges where the buyer paid but never returned to the thank-you page">
            <RefreshCw className={`w-4 h-4 ${reconciling ? 'animate-spin' : ''}`} /> {reconciling ? 'Reconciling…' : 'Reconcile'}
          </button>
          <button onClick={exportCsv} className="flex items-center gap-2 text-sm bg-[#0B2240] text-white rounded-lg px-3 py-1.5 hover:opacity-90" data-testid="billing-export-csv">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {msg && <div className="text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-lg px-3 py-2" data-testid="billing-flash">{msg}</div>}

      {/* summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Revenue" value={summary ? money(summary.revenue, 'usd') : '—'} Icon={DollarSign} accent="text-emerald-500" />
        <Stat label="Paid" value={summary?.paid ?? '—'} Icon={CheckCircle2} accent="text-emerald-500" />
        <Stat label="Pending" value={summary?.pending ?? '—'} Icon={Clock} accent="text-amber-500" />
        <Stat label="Refunded" value={summary?.refunded ?? '—'} Icon={RotateCcw} accent="text-rose-500" />
      </div>

      {/* filter */}
      <div className="flex items-center gap-2">
        {[['', 'All'], ['paid', 'Paid'], ['pending', 'Pending'], ['refunded', 'Refunded']].map(([k, lbl]) => (
          <button key={k} onClick={() => setStatusFilter(k)} className={`px-3 py-1.5 text-sm rounded-lg border ${statusFilter === k ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`} data-testid={`billing-filter-${k || 'all'}`}>{lbl}</button>
        ))}
      </div>

      {loading ? <div className="text-sm text-gray-400 py-10 text-center" data-testid="billing-loading">Loading…</div> : (
        <div className="overflow-x-auto border border-gray-200 rounded-xl">
          <table className="w-full text-sm" data-testid="billing-table">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr>
              {['Product', 'Client', 'Amount', 'Status', 'Card', 'Client?', 'Date', 'Actions'].map((h) => <th key={h} className="text-left font-semibold px-3 py-2.5">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {payments.map((p) => (
                <tr key={p.session_id} className="hover:bg-gray-50" data-testid={`billing-row-${p.session_id}`}>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#0B2240]/5 text-[#0B2240] border border-[#0B2240]/15 rounded-full px-2 py-0.5">
                      <CreditCard className="w-3 h-3" /> {p.product_label}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 font-medium text-gray-900">{p.client_name || '—'}<div className="text-xs text-gray-400">{p.email}</div></td>
                  <td className="px-3 py-2.5 font-semibold text-gray-900 tabular-nums">{money(p.amount, p.currency)}</td>
                  <td className="px-3 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[p.payment_status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>{p.payment_status}</span></td>
                  <td className="px-3 py-2.5 text-gray-600">{p.card_brand ? `${p.card_brand} ···· ${p.card_last4}` : '—'}</td>
                  <td className="px-3 py-2.5">{p.became_client ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><ShieldCheck className="w-3.5 h-3.5" /> Client</span> : <span className="text-xs text-gray-400">Lead</span>}</td>
                  <td className="px-3 py-2.5 text-gray-500 text-xs">{p.created_at ? new Date(p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</td>
                  <td className="px-3 py-2.5">
                    {p.payment_status === 'paid' ? (
                      <button onClick={() => refund(p)} className="text-xs border border-rose-300 text-rose-700 rounded px-2 py-1 hover:bg-rose-50" data-testid={`billing-refund-${p.session_id}`}>Refund</button>
                    ) : p.payment_status === 'refunded' ? (
                      <span className="text-xs text-gray-400">Refunded</span>
                    ) : <span className="text-xs text-gray-300">—</span>}
                  </td>
                </tr>
              ))}
              {!payments.length && <tr><td colSpan={8} className="text-center text-gray-400 py-8" data-testid="billing-empty">No transactions yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
